import {
	ComponentProps,
	Dispatch,
	FC,
	SetStateAction,
	useCallback,
	useRef,
	useState,
} from "react"
import { useGlobalContext } from "src/components/pages/GlobalContext"
import { IJobWorkItemSlipV2, IProductV1 } from "src/domain/entities"
import { itemSlipNextNoGetService } from "src/domain/services/itemSlip/itemSlipNextNoGetService"
import { jobWorkItemSlipAddService } from "src/domain/services/jobWorkItem/slip/jobWorkItemSlipAddService"
import { useAuth } from "src/hooks"
import { getErrorMessage } from "src/utils/helpers"
import { FormikOnSubmit } from "src/utils/types"
import { IJobWorkItemSlipBulkAddFormFields } from "./IJobWorkItemSlipBulkAddFormFields"
import { JobWorkItemSlipBulkAddDrawerFormView } from "./JobWorkItemSlipBulkAddDrawerFormView"

interface Props
	extends Omit<
		ComponentProps<typeof JobWorkItemSlipBulkAddDrawerFormView>,
		| "handleSubmit"
		| "fetchSlipNextNo"
		| "productList"
		| "selectedProduct"
		| "fetchProductList"
		| "fetchPartyList"
		| "itemType"
		| "subPartyList"
		| "productList"
		| "submissionProgress"
		| "setSubmissionProgress"
	> {
	onSuccess: (itemSlips: IJobWorkItemSlipV2[]) => void
}

export const JobWorkItemSlipBulkAddDrawerFormController: FC<Props> = ({
	onSuccess,
	defaultSelectedSubParty,
	...rest
}) => {
	const {
		token,
		company: { defaultPrefix },
	} = useAuth()

	const [submissionProgress, setSubmissionProgress] = useState<{
		isSubmitting: boolean
		successfulCount: number
		failedCount: number
		totalCount: number
	}>({
		isSubmitting: false,
		successfulCount: 0,
		failedCount: 0,
		totalCount: 0,
	})
	const {
		subPartyList,
		fetchSubPartyList,
		productList,
		isProductListLoading,
		fetchProductList,
	} = useGlobalContext()
	const defaultSelectedProduct = useRef<IProductV1>()

	const fetchSlipNextNo = useCallback(
		async (
			setNextSlipNo: Dispatch<SetStateAction<number>>,
			payload: { issueDate: string; prefix: string },
		) => {
			const response = await itemSlipNextNoGetService(payload, token)
			setNextSlipNo(response.nextSlipNo)
		},
		[token],
	)

	const handleSubmit = useCallback<FormikOnSubmit<IJobWorkItemSlipBulkAddFormFields>>(
		async (values, { setErrors, setFieldValue }) => {
			try {
				const itemSlips: IJobWorkItemSlipV2[] = []
				const remainingEntries = [...values.entries]
				const totalCount = remainingEntries.length
				let successfulCount = 0
				let failedCount = 0

				// Start submission progress
				setSubmissionProgress({
					isSubmitting: true,
					successfulCount: 0,
					failedCount: 0,
					totalCount,
				})

				// Process each entry and call the API
				for (let i = 0; i < remainingEntries.length; i++) {
					const entry = remainingEntries[i]
					const payload = {
						itemType: values.itemType,
						subPartyId: values.subPartyId,
						productId: entry.productId,
						issueDate: values.issueDate,
						prefix: values.prefix,
						slipNo: entry.slipNo,
						grossQuantity: entry.grossQuantity,
						bagQuantity: entry.bagQuantity,
						workRate: entry.workRate,
						marketRate: entry.marketRate,
						lossPercentage: entry.lossPercentage,
						note: values.note,
						isLossInclusive: values.isLossInclusive,
						includeMarketRate: values.includeMarketRate,
						include: values.include,
					}

					try {
						const itemSlip = await jobWorkItemSlipAddService(payload, token)
						itemSlips.push(itemSlip)
						successfulCount++

						// Remove the successfully submitted entry from the form
						const updatedEntries = remainingEntries.filter(
							(_, index) => index !== i,
						)
						setFieldValue("entries", updatedEntries)

						// Update the remaining entries array for next iteration
						remainingEntries.splice(i, 1)
						i-- // Adjust index since we removed an element

						// Update progress
						setSubmissionProgress((prev) => ({
							...prev,
							successfulCount,
							failedCount,
						}))
					} catch (entryError) {
						failedCount++
						// Update progress
						setSubmissionProgress((prev) => ({
							...prev,
							failedCount,
						}))
						// Continue with next entry, don't stop the process
						console.error(`Failed to submit entry ${i + 1}:`, entryError)
					}
				}

				// If we have any successful submissions, call onSuccess
				if (itemSlips.length > 0) {
					onSuccess(itemSlips)
				}

				// Only close if all entries were successful
				if (remainingEntries.length === 0) {
					setSubmissionProgress((prev) => ({ ...prev, isSubmitting: false }))
					rest.onClose()
				} else {
					// Show error message for failed entries
					setErrors({
						note: `${remainingEntries.length} entries failed to submit. Please try again.`,
					})
				}
			} catch (err) {
				setSubmissionProgress((prev) => ({ ...prev, isSubmitting: false }))
				setErrors({ note: getErrorMessage(err) })
			}
		},
		[onSuccess, rest, token],
	)

	return (
		<JobWorkItemSlipBulkAddDrawerFormView
			{...rest}
			subPartyList={subPartyList}
			productList={productList}
			isProductListLoading={isProductListLoading}
			defaultSelectedSubParty={defaultSelectedSubParty}
			defaultPrefix={defaultPrefix ?? "SL"}
			fetchSlipNextNo={fetchSlipNextNo}
			handleSubmit={handleSubmit}
			onProductAddSuccess={fetchProductList}
			onPartyAddSuccess={fetchSubPartyList}
			defaultSelectedProduct={defaultSelectedProduct}
			submissionProgress={submissionProgress}
			setSubmissionProgress={setSubmissionProgress}
		/>
	)
}
